import { Injectable } from '@angular/core';
import { Role, User } from '@core/model/user.model';
import { FlamingoHttpService } from '@flamingo/service/flamingo-http.service';
import { NamespaceModel } from '@x/model/namespace.model';
import { GlobalContextService } from '@x/service/global-context.service';
import { GlobalNamespaceService } from '@x/service/global-namespace.service';
import { BehaviorSubject, forkJoin, Observable, switchMap } from 'rxjs';
import { filter, first, map, tap } from 'rxjs/operators';
import { ContextModel } from '../../app/children/main/children/context/context.model';
import { LoggedUser, Permissions } from '../model/logged-user.model';

@Injectable({
  providedIn: 'root',
})
export class LoggedUserService {
  private _user$: BehaviorSubject<LoggedUser | null> = new BehaviorSubject<LoggedUser | null>(null);
  public user$: Observable<LoggedUser> = this._user$.asObservable()
    .pipe(
      filter((user: LoggedUser | null): user is LoggedUser => !!user),
    );

  private _namespaces$: BehaviorSubject<NamespaceModel[]> = new BehaviorSubject<NamespaceModel[]>([]);
  public namespaces$: Observable<NamespaceModel[]> = this._namespaces$.asObservable()
    .pipe(
      map(namespaces => namespaces
        .sort((n1, n2) => n1.description.toLowerCase() > n2.description.toLowerCase() ? 1 : -1),
      ),
    );

  public namespacesByGroup$: Observable<NamespaceModel[]> = this.namespaces$
    .pipe(
      map(namespaces => namespaces
        .sort((n1, n2) => {
          if ((n1.groupName || '').toLowerCase() === (n2.groupName || '').toLowerCase()) {
            return 0;
          }
          return (n1.groupName || '').toLowerCase() > (n2.groupName || '').toLowerCase() ? 1 : -1;
        }),
      ),
    );

  private _contexts$: BehaviorSubject<ContextModel[]> = new BehaviorSubject<ContextModel[]>([]);
  public contexts$: Observable<ContextModel[]> = this._contexts$.asObservable();

  constructor(
    private flamingoHttpService: FlamingoHttpService,
    private namespaceService: GlobalNamespaceService,
    private contextService: GlobalContextService,
  ) {
  }

  get user(): LoggedUser | null {
    return this._user$.value;
  }

  get namespaces(): NamespaceModel[] {
    return this._namespaces$.value;
  }

  get contexts(): ContextModel[] {
    return this._contexts$.value;
  }

  public fetch(): Observable<LoggedUser> {
    return forkJoin({
      namespaces: this.namespaceService.fetch()
        .pipe(
          // map(namespaces => namespaces
          //   .sort((n1, n2) => n1.description.toLowerCase() > n2.description.toLowerCase() ? 1 : -1)
          //   .sort((n1, n2) => {
          //     if (n1.groupName?.toLowerCase() === n2.groupName?.toLowerCase()) {
          //       return 0;
          //     }
          //     return (n1.groupName || '').toLowerCase() > (n2.groupName || '').toLowerCase() ? 1 : -1;
          //   }),
          // ),
        ),
      contexts: this.contextService.fetch(),
    })
      .pipe(
        switchMap(res => this.flamingoHttpService.get<User>(`/me`)
          .pipe(
            tap(user => {
              // Set namespaces
              this._namespaces$.next(res.namespaces
                // hide disabled namespaces for everyone
                .filter(n => !!n.enabled)
                // hide not assigned namespaces
                .filter(n => user.namespaces.includes(n.namespace)),
              );
              // Set contexts
              this._contexts$.next(res.contexts
                .filter(c => (user.metadata.contexts || []).includes(c.systemName)));
            }),
          )),
        first(),
        map(user => ({ ...user, role: { name: user.role, permissions: [] } } as LoggedUser)),
        // Set base permission
        map(user => {
          switch (user.role.name) {
            case Role.ADMIN:
              user.role.permissions.push(
                { name: Permissions.canManageDocuments },
                { name: Permissions.canUnlockDocuments },
                { name: Permissions.canManageFavDocuments },
                { name: Permissions.canCompileDocuments },
                { name: Permissions.canManageUsers },
                { name: Permissions.canManageCompanies },
                { name: Permissions.canManagePipelines },
                { name: Permissions.canManageJobs },
                { name: Permissions.canManageSelf },
                { name: Permissions.canDowngradeRole },
                { name: Permissions.canSeeHiddenPipelines },
                { name: Permissions.canManageNamespaces },
                { name: Permissions.canManageTags },
                { name: Permissions.canManageContexts },
                { name: Permissions.canUseAiAssistant },
                { name: Permissions.canUseAiGeneration },
                { name: Permissions.canUploadFiles },
                { name: Permissions.canManageSysParameters },
                { name: Permissions.canSubscribeToNamespaces },
                { name: Permissions.canManageAggregations },
              );
              break;
            case Role.OPERATOR:
              user.role.permissions.push(
                { name: Permissions.canUnlockDocuments },
                { name: Permissions.canLockDocuments },
                { name: Permissions.canManageFavDocuments },
                { name: Permissions.canManageOwnDocuments },
                { name: Permissions.canCompileDocuments },
                { name: Permissions.canManageCompanies },
                { name: Permissions.canManageSelf },
                { name: Permissions.canUseAiAssistant },
                { name: Permissions.canUploadFiles },
                { name: Permissions.canManageTags },
                { name: Permissions.canSubscribeToNamespaces },
              );
              if (user.metadata.showHiddenPipelines) {
                user.role.permissions.push(
                  { name: Permissions.canSeeHiddenPipelines },
                );
              }
              break;
            case Role.USER:
              user.role.permissions.push(
                { name: Permissions.canManageSelf },
                { name: Permissions.canManageFavDocuments },
                { name: Permissions.canUseAiAssistant },
                { name: Permissions.canUploadFiles },
                { name: Permissions.canSubscribeToNamespaces },
              );
              if (user.metadata.showHiddenPipelines) {
                user.role.permissions.push(
                  { name: Permissions.canSeeHiddenPipelines },
                );
              }
              break;
            default:
              user.role.permissions.push(
                { name: Permissions.canManageSelf },
              );
              break;
          }
          if (user.role.name !== user.originalRole) {
            user.role.permissions.push(
              { name: Permissions.canRestoreRole },
            );
          }
          return user;
        }),
        tap((user) => this._user$.next(user)),
      );
  }

  public clear(): void {
    this._user$.next(null);
  }

  can(permission: Permissions | Permissions[]): boolean {
    if (!Array.isArray(permission)) {
      permission = [permission];
    }
    return permission.every(p => this.user?.role?.permissions.map(v => v.name).includes(p));
  }

  canAtLeastOne(permission: Permissions | Permissions[]): boolean {
    if (!Array.isArray(permission)) {
      permission = [permission];
    }
    return permission.some(p => this.user?.role?.permissions.map(v => v.name).includes(p));
  }

  cannot(permission: Permissions | Permissions[]): boolean {
    if (!Array.isArray(permission)) {
      permission = [permission];
    }
    return !permission.every(p => this.user?.role?.permissions.map(v => v.name).includes(p));
  }

}
